import { FunctionComponent, useCallback, useMemo } from "react";
import styled from "styled-components";
import CustomModal from "../../CustomModal";
import Column from "../../../../components/Column";
import Row from "../../../../components/Row";
import Text from "../../../../components/Text";
import { styles } from "../../../../styles/themes/style";
import moment from "moment";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import { AppRoutes } from "../../../../interfaces/store/appStore";
import { useNavigate } from "react-router-dom";

interface IHubspotExportResultModalProps {
  senderName?: string;
  exportType: string;
  total?: number;
  successful?: number;
  failed?: number;
  createdAt?: string;
  failedList?: string;
}

const TYPE_LABEL_MAP: Record<string, string> = {
  contacts: 'imported',
  list: 'added to list',
  workflow: 'enrolled in workflow',
  deals: 'created as deals',
};

const ACTION_LABEL_MAP: Record<string, string> = {
  contacts: 'finished importing contacts',
  list: 'finished adding contacts to a list',
  workflow: 'finished enrolling contacts in a workflow',
  deals: 'finished creating deals',
};

const HubspotExportResultModal: FunctionComponent<IHubspotExportResultModalProps> = ({
  senderName,
  exportType,
  total = 0,
  successful = 0,
  failed = 0,
  createdAt,
  failedList,
}) => {
  const { hideModal } = useAppHandlers();
  const navigate = useNavigate();
  const { filterConnectionsByIdsHandler } = useConnectionsHandlers();

  const actionLabel = ACTION_LABEL_MAP[exportType] || 'finished the export';
  const heading = "HubSpot export complete! 🎉";
  const subtitle = senderName
    ? `${senderName} ${actionLabel}`
    : `${actionLabel.charAt(0).toUpperCase()}${actionLabel.slice(1)}`;
  const formattedDate = createdAt ? moment(createdAt).fromNow() : undefined;
  const failedConnectionIds = useMemo(() => {
    if (!failedList) {
      return [] as string[];
    }

    return failedList
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
  }, [failedList]);

  const isFailedClickable = failed > 0 && failedConnectionIds.length > 0;

  const handleFailedClick = useCallback(() => {
    if (!isFailedClickable) {
      return;
    }
    filterConnectionsByIdsHandler(failedConnectionIds);
    hideModal();
     navigate(AppRoutes.CONNECTIONS);
  }, [failedConnectionIds, filterConnectionsByIdsHandler, hideModal, isFailedClickable]);

  return (
    <CustomModal headingText={heading} closeButton>
      <Column gap="16px" style={{ width: "100%" }}>


        <Column gap="8px">
          <HeaderRow alignItems spaceBetween>
            <Text $paragraph1>Overview</Text>
            <ValuesHeader>
              <RowHeader />
            </ValuesHeader>
          </HeaderRow>

          <RowDivider />

          <DataRow alignItems spaceBetween>
            <Text $paragraph1 $bold>Total connections</Text>
            <ValueCell>
              <Text $paragraph1 $bold >{total}</Text>
            </ValueCell>
          </DataRow>
          <DataRow alignItems spaceBetween>
            <Text>Exported</Text>
            <ValueCell>
              <Text $paragraph1 $bold>{successful}</Text>
            </ValueCell>
          </DataRow>
          {isFailedClickable ? (
            <ClickableDataRow alignItems spaceBetween onClick={handleFailedClick}>
              <ClickableLabel>Failed</ClickableLabel>
              <ValueCell>
                <ClickableLabel>{failed}</ClickableLabel>
              </ValueCell>
            </ClickableDataRow>
          ) : (
            <DataRow alignItems spaceBetween>
              <Text>Failed</Text>
              <ValueCell>
                <Text $paragraph1 $bold>{failed}</Text>
              </ValueCell>
            </DataRow>
          )}
        </Column>
      </Column>
    </CustomModal>
  );
};
const HeaderRow = styled(Row)`
  width: 100%;
`;

const ValuesHeader = styled(Row)`
  gap: 16px;
  align-items: center;
`;

const RowHeader = styled.div`
  width: 58px;
`;

const RowDivider = styled(Row)`
  width: 100%;
  height: 1px;
  background-color: ${styles.colors.black200};
`;

const DataRow = styled(Row)`
  width: 100%;
`;

const ValueCell = styled(Row)`
  width: 58px;
  display: flex;
  justify-content: flex-end;
`;

const ValueText = styled(Text)`
  font-weight: 600;
`;

const ClickableLabel = styled(Text)`
  font-weight: 600;
  font-size: 16px;
  &:hover {
    text-decoration: underline;
  }
`;

const ClickableDataRow = styled(DataRow)`
  cursor: pointer;

  &:hover ${ClickableLabel} {
    text-decoration: underline;
  }
`;

export default HubspotExportResultModal;
